import { ApolloError, useMutation, useQuery } from '@apollo/client';
import {
  Filter,
  Lock,
  Monitor,
  RefreshCw,
  Search,
  SearchX,
  ShieldCheck,
  User,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AUDIT_ALL_ACTIONS, AUDIT_CATALOG, AUDIT_MODULES } from './audit-catalog';
import { buildAuditPayloadRows, type AuditPayloadDeps } from './audit-payload';
import { auditModuleStyle } from './audit-style';
import {
  AUDIT_LOGS_QUERY,
  SET_AUDIT_PIN_MUTATION,
  START_STEP_UP_AUDIT_SESSION_MUTATION,
} from './audit.gql';
import { useAuditLabels } from './useAuditLabels';

import { ACTIVE_WORKSTATIONS_QUERY } from '@/modules/settings/settings.gql';
import { ADMIN_USERS_QUERY } from '@/modules/users-admin/users-admin.gql';
import { formatCentsString } from '@/shared/lib/money';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { useAuditStepUpStore } from '@/stores/auditStepUpStore';

interface AuditLog {
  id: string;
  actor_user_id: string | null;
  actor_role: string | null;
  workstation_id: string | null;
  module: string;
  action: string;
  reference_kind: string | null;
  reference_id: string | null;
  amount_cents: string | null;
  reason: string | null;
  previous_state_json: string | null;
  new_state_json: string | null;
  metadata_json: string | null;
  correlation_id: string | null;
  ip: string | null;
  created_at: string;
}

interface AuditLogsResponse {
  auditLogs: { items: AuditLog[]; page: { total: number; has_more: boolean } };
}

interface UserLookupResponse {
  adminUsers: { items: { id: string; full_name: string }[] };
}

interface WorkstationLookupResponse {
  workstations: { items: { id: string; display_name: string }[] };
}

const PAGE_SIZE = 50;
const FILTER_DEBOUNCE_MS = 400;
const SELECT_CLASS = 'border-input bg-background h-10 w-full rounded-md border px-2 text-sm';

interface AppliedFilters {
  module: string;
  action: string;
  actor: string;
  from: string; // YYYY-MM-DD from <input type="date">, '' = unbounded
  to: string;
}

const EMPTY_FILTERS: AppliedFilters = { module: '', action: '', actor: '', from: '', to: '' };

/** Local-day bounds: the picked day is the operator's calendar day, not UTC. */
function dayStartIso(day: string): string | null {
  return day ? new Date(`${day}T00:00:00`).toISOString() : null;
}

function dayEndIso(day: string): string | null {
  return day ? new Date(`${day}T23:59:59.999`).toISOString() : null;
}

export function AuditPage(): JSX.Element {
  const { t } = useTranslation();
  const get_token = useAuditStepUpStore((s) => s.getActiveToken);
  const set_token = useAuditStepUpStore((s) => s.setToken);
  const clear_token = useAuditStepUpStore((s) => s.clear);

  const [active_token, setActiveTokenLocal] = useState<string | null>(get_token());
  const [show_pin, setShowPin] = useState(active_token === null);
  const [show_set_pin, setShowSetPin] = useState(false);
  // What the inputs show (updates on every keystroke) vs what the query uses (settles after a pause).
  const [filter_module, setFilterModule] = useState('');
  const [filter_action, setFilterAction] = useState('');
  const [filter_actor, setFilterActor] = useState('');
  const [filter_from, setFilterFrom] = useState('');
  const [filter_to, setFilterTo] = useState('');
  const [applied, setApplied] = useState<AppliedFilters>(EMPTY_FILTERS);
  // YYYY-MM-DD strings compare chronologically, so a plain string check is enough.
  const range_invalid = filter_from !== '' && filter_to !== '' && filter_from > filter_to;
  const [page_index, setPageIndex] = useState(0);
  const [error_text, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    // An inverted range would just return nothing; keep the last valid result until it is fixed.
    if (range_invalid) return undefined;
    const timer = setTimeout(() => {
      setApplied((prev) =>
        prev.module === filter_module &&
        prev.action === filter_action &&
        prev.actor === filter_actor &&
        prev.from === filter_from &&
        prev.to === filter_to
          ? prev
          : {
              module: filter_module,
              action: filter_action,
              actor: filter_actor,
              from: filter_from,
              to: filter_to,
            },
      );
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filter_module, filter_action, filter_actor, filter_from, filter_to, range_invalid]);

  // A changed filter means a different result set: always restart from the first page.
  useEffect(() => {
    setPageIndex(0);
  }, [applied]);

  useEffect(() => {
    const tick = setInterval(() => {
      const t_now = get_token();
      if (t_now !== active_token) {
        setActiveTokenLocal(t_now);
        if (!t_now) setShowPin(true);
      }
    }, 30_000);
    return () => clearInterval(tick);
  }, [active_token, get_token]);

  const {
    data: fresh,
    previousData,
    loading,
    refetch,
  } = useQuery<AuditLogsResponse>(AUDIT_LOGS_QUERY, {
    variables: {
      offset: page_index * PAGE_SIZE,
      limit: PAGE_SIZE,
      module: applied.module || null,
      action: applied.action || null,
      actor_user_id: applied.actor || null,
      from: dayStartIso(applied.from),
      to: dayEndIso(applied.to),
    },
    fetchPolicy: 'network-only',
    skip: !active_token,
    // eslint-disable-next-line @typescript-eslint/naming-convention
    context: active_token ? { headers: { 'X-Audit-Step-Up': active_token } } : undefined,
  });

  // Name lookups so rows show people / stations instead of UUIDs. Best-effort: a failed lookup
  // just falls back to a neutral label, it never blocks the log.
  const { data: users_data } = useQuery<UserLookupResponse>(ADMIN_USERS_QUERY, {
    variables: { offset: 0, limit: 200 },
    skip: !active_token,
    errorPolicy: 'ignore',
  });
  const { data: stations_data } = useQuery<WorkstationLookupResponse>(ACTIVE_WORKSTATIONS_QUERY, {
    variables: { branch_id: null },
    skip: !active_token,
    errorPolicy: 'ignore',
  });
  const user_options = users_data?.adminUsers.items ?? [];
  const user_names = new Map(user_options.map((u) => [u.id, u.full_name]));
  const station_names = new Map(
    (stations_data?.workstations.items ?? []).map((w) => [w.id, w.display_name]),
  );
  const { moduleLabel, actionLabel, referenceLabel, roleLabel, fieldLabel, valueLabel } =
    useAuditLabels();
  const payload_deps: AuditPayloadDeps = {
    fieldLabel,
    valueLabel,
    userName: (id) => user_names.get(id) ?? null,
    formatMoney: formatCentsString,
    formatDate: fmtDate,
    yes: t('common.yes'),
    no: t('common.no'),
    empty: t('audit.value_empty'),
  };
  const action_options = filter_module ? (AUDIT_CATALOG[filter_module] ?? []) : AUDIT_ALL_ACTIONS;
  const sorted_action_options = [...action_options].sort((a, b) =>
    actionLabel(a).localeCompare(actionLabel(b), 'es'),
  );
  const actorName = (row: AuditLog): string => {
    if (!row.actor_user_id) return t('audit.actor_system');
    return user_names.get(row.actor_user_id) ?? t('audit.actor_unknown');
  };

  const data = fresh ?? previousData;
  const items = data?.auditLogs.items ?? [];
  const total = data?.auditLogs.page.total ?? 0;
  const has_more = data?.auditLogs.page.has_more ?? false;
  const range_from = items.length === 0 ? 0 : page_index * PAGE_SIZE + 1;
  const range_to = page_index * PAGE_SIZE + items.length;

  const onLogout = useCallback((): void => {
    clear_token();
    setActiveTokenLocal(null);
    setShowPin(true);
  }, [clear_token]);

  const onUnlocked = useCallback(
    (token: string, expires_in_seconds: number): void => {
      set_token(token, expires_in_seconds);
      setActiveTokenLocal(token);
      setShowPin(false);
      setErrorText(null);
    },
    [set_token],
  );

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">{t('audit.title')}</h2>
          <p className="text-muted-foreground text-sm">{t('audit.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setShowSetPin(true)}>
            <ShieldCheck className="h-4 w-4" aria-hidden />
            <span>{t('audit.set_pin')}</span>
          </Button>
          {active_token ? (
            <Button type="button" variant="ghost" size="sm" onClick={onLogout}>
              <Lock className="h-4 w-4" aria-hidden />
              <span>{t('audit.relock')}</span>
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={() => setShowPin(true)}>
              <ShieldCheck className="h-4 w-4" aria-hidden />
              <span>{t('audit.unlock')}</span>
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              void refetch();
            }}
            disabled={!active_token}
          >
            <RefreshCw className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      </header>

      {error_text ? (
        <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-3 text-sm">
          {error_text}
        </div>
      ) : null}

      {!active_token ? (
        <Card>
          <CardContent className="text-muted-foreground flex flex-col items-center gap-3 py-12 text-center text-sm">
            <span className="bg-muted flex h-14 w-14 items-center justify-center rounded-full">
              <Lock className="h-6 w-6" aria-hidden />
            </span>
            {t('audit.locked_body')}
            <Button type="button" size="sm" onClick={() => setShowPin(true)}>
              <ShieldCheck className="h-4 w-4" aria-hidden />
              <span>{t('audit.unlock')}</span>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="grid gap-3 pt-4 md:grid-cols-3">
              <div className="text-muted-foreground flex items-center justify-between md:col-span-3">
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide uppercase">
                  <Filter className="h-3.5 w-3.5" aria-hidden />
                  {t('audit.filters')}
                </span>
                {filter_module || filter_action || filter_actor || filter_from || filter_to ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setFilterModule('');
                      setFilterAction('');
                      setFilterActor('');
                      setFilterFrom('');
                      setFilterTo('');
                    }}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                    <span>{t('audit.filter_clear_all')}</span>
                  </Button>
                ) : null}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('audit.filter_module')}</Label>
                <select
                  className={SELECT_CLASS}
                  value={filter_module}
                  onChange={(e) => {
                    const next = e.target.value;
                    setFilterModule(next);
                    // An action that belongs to another module can no longer match: drop it.
                    if (
                      next &&
                      filter_action &&
                      !(AUDIT_CATALOG[next] ?? []).includes(filter_action)
                    ) {
                      setFilterAction('');
                    }
                  }}
                >
                  <option value="">{t('audit.filter_all_modules')}</option>
                  {AUDIT_MODULES.map((m) => (
                    <option key={m} value={m}>
                      {moduleLabel(m)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('audit.filter_action')}</Label>
                <select
                  className={SELECT_CLASS}
                  value={filter_action}
                  onChange={(e) => setFilterAction(e.target.value)}
                >
                  <option value="">{t('audit.filter_all_actions')}</option>
                  {sorted_action_options.map((a) => (
                    <option key={a} value={a}>
                      {actionLabel(a)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('audit.filter_actor')}</Label>
                <div className="flex gap-2">
                  <select
                    className={SELECT_CLASS}
                    value={filter_actor}
                    onChange={(e) => setFilterActor(e.target.value)}
                  >
                    <option value="">{t('audit.filter_all_actors')}</option>
                    {user_options.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.full_name}
                      </option>
                    ))}
                  </select>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      // Skip the pause: apply what is typed now (a no-op re-query if already applied).
                      if (!range_invalid) {
                        setApplied({
                          module: filter_module,
                          action: filter_action,
                          actor: filter_actor,
                          from: filter_from,
                          to: filter_to,
                        });
                      }
                      void refetch();
                    }}
                  >
                    <Search className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('audit.filter_from')}</Label>
                <Input
                  type="date"
                  value={filter_from}
                  max={filter_to || undefined}
                  onChange={(e) => setFilterFrom(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('audit.filter_to')}</Label>
                <Input
                  type="date"
                  value={filter_to}
                  min={filter_from || undefined}
                  onChange={(e) => setFilterTo(e.target.value)}
                />
              </div>
              {filter_from || filter_to ? (
                <div className="flex items-end">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setFilterFrom('');
                      setFilterTo('');
                    }}
                  >
                    {t('audit.filter_clear_dates')}
                  </Button>
                </div>
              ) : null}
              {range_invalid ? (
                <p className="text-destructive text-xs md:col-span-3">
                  {t('audit.error_range_invalid')}
                </p>
              ) : null}
            </CardContent>
          </Card>

          {items.length === 0 && loading ? (
            <ul className="space-y-2" aria-busy="true">
              {[0, 1, 2, 3, 4].map((i) => (
                <li key={i} className="bg-muted/60 h-16 animate-pulse rounded-lg" />
              ))}
            </ul>
          ) : items.length === 0 ? (
            <Card>
              <CardContent className="text-muted-foreground flex flex-col items-center gap-2 py-10 text-center text-sm">
                <SearchX className="h-8 w-8 opacity-50" aria-hidden />
                {t('audit.empty')}
              </CardContent>
            </Card>
          ) : (
            <div className={loading ? 'space-y-4 opacity-60 transition-opacity' : 'space-y-4'}>
              {groupByDay(items).map((group) => (
                <section key={group.day} className="space-y-2">
                  <h3 className="text-muted-foreground bg-background/90 sticky top-0 z-10 py-1 text-xs font-semibold tracking-wide uppercase backdrop-blur">
                    {group.label}
                  </h3>
                  <ul className="space-y-2">
                    {group.rows.map((row) => (
                      <li key={row.id}>
                        <AuditRow
                          row={row}
                          actor={actorName(row)}
                          station={
                            row.workstation_id
                              ? (station_names.get(row.workstation_id) ??
                                t('audit.workstation_unknown'))
                              : null
                          }
                          labels={{ moduleLabel, actionLabel, referenceLabel, roleLabel }}
                          payloadDeps={payload_deps}
                        />
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          )}

          {total > 0 ? (
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-muted-foreground">
                {t('audit.page_range', { from: range_from, to: range_to, total })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={page_index === 0 || loading}
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                >
                  {t('audit.page_prev')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={!has_more || loading}
                  onClick={() => setPageIndex((p) => p + 1)}
                >
                  {t('audit.page_next')}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}

      {show_pin ? (
        <StepUpPinDialog
          on_close={() => setShowPin(false)}
          on_unlocked={onUnlocked}
          on_error={setErrorText}
        />
      ) : null}

      {show_set_pin ? (
        <SetAuditPinDialog
          on_close={() => setShowSetPin(false)}
          on_done={() => {
            setShowSetPin(false);
            setErrorText(t('audit.pin_set_ok'));
          }}
          on_error={setErrorText}
        />
      ) : null}
    </div>
  );
}

interface DayGroup {
  day: string;
  label: string;
  rows: AuditLog[];
}

/** Consecutive rows of the same local calendar day share one heading (list is newest-first). */
function groupByDay(rows: AuditLog[]): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const row of rows) {
    const d = new Date(row.created_at);
    const day = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    const last = groups[groups.length - 1];
    if (last?.day === day) {
      last.rows.push(row);
    } else {
      groups.push({
        day,
        label: d.toLocaleDateString('es-MX', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }),
        rows: [row],
      });
    }
  }
  return groups;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('es-MX', { timeStyle: 'medium' });
}

function AuditRow({
  row,
  actor,
  station,
  labels,
  payloadDeps,
}: {
  row: AuditLog;
  actor: string;
  station: string | null;
  labels: Pick<
    ReturnType<typeof useAuditLabels>,
    'moduleLabel' | 'actionLabel' | 'referenceLabel' | 'roleLabel'
  >;
  payloadDeps: AuditPayloadDeps;
}): JSX.Element {
  const { t } = useTranslation();
  const style = auditModuleStyle(row.module);
  const Icon = style.icon;
  return (
    <Card className="transition-shadow hover:shadow-md">
      <CardContent className="flex gap-3 p-3">
        <span
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${style.chip}`}
          aria-hidden
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1 space-y-1.5">
          <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
            <div className="min-w-0 space-y-0.5">
              <p className="text-sm leading-tight font-medium">{labels.actionLabel(row.action)}</p>
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant="muted">{labels.moduleLabel(row.module)}</Badge>
                {row.reference_kind ? (
                  <span className="text-muted-foreground text-xs">
                    {labels.referenceLabel(row.reference_kind)}
                    {row.reference_id ? (
                      <span className="font-mono"> · #{row.reference_id.slice(0, 8)}</span>
                    ) : null}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col items-end gap-0.5">
              <span className="text-muted-foreground text-xs tabular-nums">
                {fmtTime(row.created_at)}
              </span>
              {row.amount_cents ? (
                <span className="font-mono text-sm font-semibold tabular-nums">
                  {formatCentsString(row.amount_cents)}
                </span>
              ) : null}
            </div>
          </div>
          <div className="text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
            <span className="inline-flex items-center gap-1">
              <User className="h-3 w-3" aria-hidden />
              <span className="text-foreground">{actor}</span>
              {row.actor_role ? (
                <Badge variant="outline" className="px-1.5 py-0">
                  {labels.roleLabel(row.actor_role)}
                </Badge>
              ) : null}
            </span>
            {station ? (
              <span className="inline-flex items-center gap-1" title={t('audit.workstation')}>
                <Monitor className="h-3 w-3" aria-hidden />
                {station}
              </span>
            ) : null}
          </div>
          {row.reason ? (
            <p className="bg-muted/60 text-muted-foreground rounded-md px-2 py-1 text-xs italic">
              “{row.reason}”
            </p>
          ) : null}
          <PayloadDetails row={row} payloadDeps={payloadDeps} />
        </div>
      </CardContent>
    </Card>
  );
}

function PayloadDetails({
  row,
  payloadDeps,
}: {
  row: AuditLog;
  payloadDeps: AuditPayloadDeps;
}): JSX.Element | null {
  const { t } = useTranslation();
  const sections = [
    { id: 'prev', title: t('audit.payload_prev'), raw: row.previous_state_json },
    { id: 'new', title: t('audit.payload_new'), raw: row.new_state_json },
    { id: 'meta', title: t('audit.payload_meta'), raw: row.metadata_json },
  ]
    .map((s) => ({ ...s, rows: buildAuditPayloadRows(s.raw, payloadDeps) }))
    .filter((s) => s.rows.length > 0);
  if (sections.length === 0) return null;
  return (
    <details className="text-muted-foreground text-xs">
      <summary className="cursor-pointer">{t('audit.payload')}</summary>
      <div className="mt-1 grid gap-2 md:grid-cols-2">
        {sections.map((s) => (
          <div key={s.id} className="bg-muted rounded p-2">
            <p className="text-foreground mb-1 font-medium">{s.title}</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
              {s.rows.map((r) => (
                <div key={r.key} className="contents">
                  <dt>{r.label}</dt>
                  <dd className="text-foreground break-words">{r.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ))}
      </div>
    </details>
  );
}

function StepUpPinDialog({
  on_close,
  on_unlocked,
  on_error,
}: {
  on_close: () => void;
  on_unlocked: (token: string, expires_in_seconds: number) => void;
  on_error: (msg: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [start, { loading }] = useMutation<{
    startStepUpAuditSession: { audit_token: string; expires_in_seconds: number };
  }>(START_STEP_UP_AUDIT_SESSION_MUTATION);

  const onClickSubmit = async (): Promise<void> => {
    on_error(null);
    try {
      const r = await start({ variables: { input: { pin } } });
      const payload = r.data?.startStepUpAuditSession;
      if (!payload) throw new Error('no_payload');
      on_unlocked(payload.audit_token, payload.expires_in_seconds);
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        if (code === 'audit_pin_invalid') return on_error(t('audit.error_pin_invalid'));
        if (code === 'audit_pin_not_set') return on_error(t('audit.error_pin_not_set'));
        return on_error(code);
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('audit.unlock_title')}</CardTitle>
          <p className="text-muted-foreground text-xs">{t('audit.unlock_hint')}</p>
        </CardHeader>
        <CardContent>
          <div className="space-y-1">
            <Label htmlFor="audit-pin">{t('audit.pin_label')}</Label>
            <Input
              id="audit-pin"
              type="password"
              autoComplete="off"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              disabled={loading}
            />
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={pin.length === 0 || loading}
          >
            {loading ? t('common.loading') : t('audit.unlock')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function SetAuditPinDialog({
  on_close,
  on_done,
  on_error,
}: {
  on_close: () => void;
  on_done: () => void;
  on_error: (msg: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [setAuditPin, { loading }] = useMutation(SET_AUDIT_PIN_MUTATION);

  const can_submit = pin.length >= 4 && pin === confirm && !loading;

  const onClickSubmit = async (): Promise<void> => {
    on_error(null);
    try {
      await setAuditPin({ variables: { input: { pin } } });
      on_done();
    } catch (err) {
      if (err instanceof ApolloError) {
        return on_error(err.graphQLErrors[0]?.message ?? err.message);
      }
      if (err instanceof Error) on_error(err.message);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-4 backdrop-blur"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{t('audit.set_pin_title')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{t('audit.pin_label')}</Label>
            <Input
              type="password"
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              disabled={loading}
            />
          </div>
          <div className="space-y-1">
            <Label>{t('audit.pin_confirm')}</Label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              disabled={loading}
            />
            {pin && confirm && pin !== confirm ? (
              <p className="text-destructive text-xs">{t('audit.error_pin_mismatch')}</p>
            ) : null}
          </div>
        </CardContent>
        <CardFooter className="justify-end gap-2">
          <Button type="button" variant="ghost" onClick={on_close} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => {
              void onClickSubmit();
            }}
            disabled={!can_submit}
          >
            {loading ? t('common.loading') : t('audit.set_pin')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'medium' });
  } catch {
    return iso;
  }
}
